import { useState } from 'react';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/Tooltip';
import {
  Target, AlertTriangle, ChevronDown, ChevronUp, CheckCircle2, Loader2,
  Layers, Globe,
} from 'lucide-react';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type { StrategicPhase, StrategicConflict, StrategicWorkstream } from '@/hooks/useStrategicStream';

export function ConflictMapperNode({ phase, conflicts, workstreams, isSmallScreen }: {
  phase: StrategicPhase; conflicts: StrategicConflict[]; workstreams: StrategicWorkstream[]; isSmallScreen: boolean;
}) {
  const isActive = phase === 'synthesis';
  const isComplete = ['stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase);
  const [isExpanded, setIsExpanded] = useState(true);

  const allKeyFindings = workstreams.flatMap(ws =>
    ws.keyFindings.map(finding => ({ workstream: ws.name, finding }))
  );
  const allSources = workstreams.flatMap(ws =>
    ws.agents.flatMap(agent => agent.sources || [])
  );
  const hasDetails = allKeyFindings.length > 0 || conflicts.length > 0 || allSources.length > 0;

  return (
    <div className={cn('mx-auto rounded-xl border backdrop-blur transition-all duration-300',
      isActive && 'animate-pulse border-orange-500/60 shadow-lg shadow-orange-500/20',
      isComplete ? 'border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-amber-500/5' : 'border-orange-500/20 bg-orange-500/5',
      isSmallScreen ? 'max-w-[340px] p-4' : 'max-w-[900px] p-6')}>

      <button
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="conflict-mapper-content"
        className={cn('w-full', hasDetails && 'cursor-pointer')}
      >
        <div className={cn('flex items-center justify-center gap-2 mb-2 text-orange-400 font-semibold uppercase', isSmallScreen ? 'text-sm' : 'text-base')}>
          {isComplete ? <CheckCircle2 className="w-5 h-5" /> : isActive ? <Loader2 className="w-5 h-5 animate-spin" /> : <Layers className="w-5 h-5" />}
          <span>Synthesis & Conflict Mapper <InfoTooltip content="Combines findings from all workstreams and flags contradictions between them for the debate to resolve." position="right" /></span>
          {hasDetails && (isExpanded ? <ChevronUp className="w-5 h-5 text-text-secondary" /> : <ChevronDown className="w-5 h-5 text-text-secondary" />)}
        </div>
        <div className={cn('text-text-secondary text-center', isSmallScreen ? 'text-xs' : 'text-sm')}>Cross-workstream analysis aggregator</div>
      </button>

      {conflicts.length > 0 && (
        <div className="flex items-center justify-center mt-3">
          <span className={cn('px-3 py-1.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400 font-medium cursor-help', isSmallScreen ? 'text-xs' : 'text-sm')}>
            {conflicts.length} Conflicts Detected
          </span>
        </div>
      )}

      {isExpanded && (
        <div id="conflict-mapper-content" role="region" className="mt-5 space-y-5 border-t border-orange-500/20 pt-5">
          {allKeyFindings.length > 0 && (
            <div>
              <div className={cn('text-orange-400/80 mb-3 flex items-center gap-2 uppercase tracking-wide font-semibold', isSmallScreen ? 'text-sm' : 'text-base')}>
                <Target className="w-5 h-5" />
                Aggregated Key Findings
              </div>
              <div className="space-y-4">
                {workstreams.filter(ws => ws.keyFindings.length > 0).map((ws, i) => (
                  <div key={i} className="rounded-lg bg-surface-light/50 border border-border/30 p-4">
                    <div className={cn('text-orange-300 mb-2 font-semibold flex items-center gap-2', isSmallScreen ? 'text-sm' : 'text-base')}>
                      <span className="w-2.5 h-2.5 rounded-full bg-orange-400" />
                      {ws.name}
                    </div>
                    <div className="max-h-[150px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-orange-500/20 scrollbar-track-transparent">
                      <ul className={cn('space-y-2', isSmallScreen ? 'text-xs' : 'text-sm')}>
                        {ws.keyFindings.map((finding, j) => (
                          <li key={j} className="flex items-start gap-2 text-text-primary leading-relaxed">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400/60 mt-2 flex-shrink-0" />
                            <MarkdownContent content={finding} className={cn('flex-1 [&_p]:inline', isSmallScreen ? 'text-xs' : 'text-sm')} inline />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {conflicts.length > 0 && (
            <div>
              <div className={cn('text-amber-400 mb-3 flex items-center gap-2 uppercase tracking-wide font-semibold', isSmallScreen ? 'text-sm' : 'text-base')}>
                <AlertTriangle className="w-5 h-5" />
                Detected Conflicts ({conflicts.length})
              </div>
              <div className="space-y-4">
                {conflicts.map((c, i) => (
                  <div key={i} className={cn(
                    'rounded-lg border p-4',
                    c.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                    c.severity === 'significant' ? 'bg-amber-500/10 border-amber-500/30' : 'bg-yellow-500/10 border-yellow-500/30'
                  )}>
                    <div className={cn('flex items-center justify-between mb-3', isSmallScreen ? 'text-sm' : 'text-base')}>
                      <div className="flex items-center gap-2">
                        <span className={cn('px-2.5 py-1 rounded font-bold text-xs uppercase',
                          c.severity === 'critical' ? 'bg-red-500/30 text-red-300' :
                          c.severity === 'significant' ? 'bg-amber-500/30 text-amber-300' : 'bg-yellow-500/30 text-yellow-300')}>
                          {c.severity}
                        </span>
                        <span className="text-text-secondary">|</span>
                        <span className="text-text-primary font-medium">{c.workstreamA}</span>
                        <span className="text-text-muted">vs</span>
                        <span className="text-text-primary font-medium">{c.workstreamB}</span>
                      </div>
                    </div>
                    <div className="max-h-[120px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-amber-500/20 scrollbar-track-transparent mb-3">
                      <div className={cn('text-text-primary leading-relaxed', isSmallScreen ? 'text-xs' : 'text-sm')}>
                        <MarkdownContent content={c.description} className={isSmallScreen ? 'text-xs' : 'text-sm'} />
                      </div>
                    </div>
                    {c.resolution && (
                      <div className="border-t border-green-500/20 pt-3 mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                          <span className={cn('text-green-400 font-semibold uppercase', isSmallScreen ? 'text-xs' : 'text-sm')}>Resolution</span>
                        </div>
                        <div className="max-h-[100px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-green-500/20 scrollbar-track-transparent">
                          <div className={cn('text-green-300/90 leading-relaxed', isSmallScreen ? 'text-xs' : 'text-sm')}>
                            <MarkdownContent content={c.resolution} className={isSmallScreen ? 'text-xs' : 'text-sm'} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {allSources.length > 0 && (
            <div>
              <div className={cn('text-cyan-400 mb-3 flex items-center gap-2 uppercase tracking-wide font-semibold', isSmallScreen ? 'text-sm' : 'text-base')}>
                <Globe className="w-5 h-5" />
                All Sources ({allSources.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {allSources.slice(0, 10).map((src, i) => (
                  <a key={i} href={src.url || '#'} target="_blank" rel="noopener noreferrer"
                    className={cn('px-3 py-1.5 rounded bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-colors truncate max-w-[250px]', isSmallScreen ? 'text-xs' : 'text-sm')}>
                    {src.title || `Source ${i + 1}`}
                  </a>
                ))}
                {allSources.length > 10 && (
                  <span className={cn('px-3 py-1.5 rounded bg-surface-raised/50 text-text-secondary', isSmallScreen ? 'text-xs' : 'text-sm')}>
                    +{allSources.length - 10} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
